// PollBox.jsx
import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { getActivePoll, votePoll } from "../services/api";

export default function PollBox({ userData }) {
    const [poll, setPoll] = useState(null);
    const [selectedOptions, setSelectedOptions] = useState([]);
    const [votingInProgress, setVotingInProgress] = useState(false);

    useEffect(() => {
        const fetchPoll = async () => {
            try {
                const response = await getActivePoll();
                console.log("Poll data:", response);
                if (response.success && response.data) {
                    setPoll(response.data);
                    setSelectedOptions([]);
                }
            } catch (error) {
                console.error("Failed to fetch poll:", error);
            }
        };
        fetchPoll();
    }, []);

    const handleOptionToggle = (optionId) => {
        if (!userData) {
            toast.error("You must be logged in to vote", {
                autoClose: 3000,
            });
            return;
        }

        if (poll.hasVoted) return;

        if (poll.multipleChoice) {
            setSelectedOptions((prev) =>
                prev.includes(optionId)
                    ? prev.filter((id) => id !== optionId)
                    : [...prev, optionId]
            );
        } else {
            setSelectedOptions([optionId]);
        }
    };

    const handleVote = async () => {
        if (!userData) {
            toast.error("You must be logged in to vote", {
                autoClose: 3000,
            });
            return;
        }

        if (selectedOptions.length === 0) {
            toast.error("Please select at least one option", {
                autoClose: 3000
            });
            return;
        }

        setVotingInProgress(true);
        try {
            await votePoll(poll.pollId, selectedOptions);
            toast.success("Vote submitted!", {
                autoClose: 3000,
            });

            const response = await getActivePoll();
            if (response.success && response.data) {
                setPoll(response.data);
                setSelectedOptions([]);
            }
        } catch (error) {
            console.error("Failed to vote:", error);
            const errorMessage = error.message || "Failed to submit vote";
            toast.error(errorMessage, {
                autoClose: 3000,
            });
        } finally {
            setVotingInProgress(false);
        }
    };

    if (!poll) return <p>There's no active poll right now!</p>;

    return (
        <div>
            <h3 className="text-base font-bold mb-2">{poll.title}</h3>
            <p className="mb-3 text-sm">{poll.question}</p>

            {poll.hasVoted ? (
                <>
                    {poll.showResults ? (
                        <div className="space-y-2 mb-3">
                            {poll.options.map((option) => (
                                <div key={option.optionId} className="bg-gray-200 rounded p-2">
                                    <div className="flex justify-between items-center mb-1">
                                        <span className="text-sm">{option.text}</span>
                                        <span className="text-sm text-gray-600">
                      {option.votes} ({option.percentage}%)
                    </span>
                                    </div>
                                    <div className="bg-gray-300 h-2 rounded overflow-hidden">
                                        <div
                                            className="bg-maroon h-full"
                                            style={{ width: `${option.percentage}%` }}
                                        />
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="space-y-2 mb-3">
                            {poll.options.map((option) => (
                                <div key={option.optionId} className="bg-gray-200 rounded p-2">
                                    <span className="text-sm">{option.text}</span>
                                </div>
                            ))}
                        </div>
                    )}
                </>
            ) : (
                <>
                    <div className="space-y-2 mb-3">
                        {poll.options.map((option) => (
                            <div key={option.optionId} className="bg-gray-200 rounded p-2">
                                <label className="flex items-center cursor-pointer">
                                    <input
                                        type={poll.multipleChoice ? "checkbox" : "radio"}
                                        name="poll-option"
                                        checked={selectedOptions.includes(option.optionId)}
                                        onChange={() => handleOptionToggle(option.optionId)}
                                        className="mr-2"
                                        disabled={!userData || votingInProgress}
                                    />
                                    <span className="text-sm">{option.text}</span>
                                </label>
                            </div>
                        ))}
                    </div>

                    {userData ? (
                        <button
                            onClick={handleVote}
                            disabled={selectedOptions.length === 0 || votingInProgress}
                            className="w-full bg-blue-300 text-white px-4 py-2 text-sm uppercase tracking-wider transition-opacity hover:opacity-90 disabled:opacity-50"
                            style={{ boxShadow: "0 3px 0 0 rgba(0, 0, 0, 0.3)" }}
                        >
                            {votingInProgress ? "Submitting..." : "Submit Vote"}
                        </button>
                    ) : (
                        <p className="text-center text-sm text-gray-600 uppercase">
                            Login to vote
                        </p>
                    )}
                </>
            )}
        </div>
    );
}
